import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { setNotificationHandler } from './services/api';
import Navbar from './components/Navbar';
import AnnouncementBanner from './components/AnnouncementBanner';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import AdminUsers from './pages/AdminUsers';
import AdminRoles from './pages/AdminRoles';
import AdminReviews from './pages/AdminReviews';
import AdminMentors from './pages/AdminMentors';
import AdminPrograms from './pages/AdminPrograms';
import AdminContacts from './pages/AdminContacts';
import AdminCareerChavdi from './pages/AdminCareerChavdi';
import AdminAnnouncement from './pages/AdminAnnouncement';

import Moderator from './pages/Moderator';
import Users from './pages/Users';
import UserForm from './pages/UserForm';
import CloudDevOpsProgram from './pages/CloudDevOpsProgram';
import ServicePage from './pages/ServicePage';
import AIDataSciencePage from './pages/AIDataSciencePage';
import GenAIPage from './pages/GenAIPage';
import DevOpsPage from './pages/DevOpsPage';
import CyberSecurityPage from './pages/CyberSecurityPage';
import CareerChavdiPage from './pages/CareerChavdiPage';

import ContactPage from './pages/ContactPage';
import AboutUsPage from './pages/AboutUsPage';
import SolutionsPage from './pages/SolutionsPage';
import SkillToDeployPage from './pages/SkillToDeployPage';

import CareersPage from './pages/CareersPage';

// ... (other imports)

// Dashboard routes that shouldn't show the main navbar
const dashboardRoutes = ['/profile', '/admin', '/moderator', '/admin/users', '/admin/roles', '/admin/reviews', '/admin/mentors', '/admin/programs', '/admin/contacts', '/admin/career-chavdi', '/admin/announcement'];

// Component to setup API notifications
const ApiNotificationSetup = () => {
  const { showError } = useNotification();

  useEffect(() => {
    setNotificationHandler(showError);
  }, [showError]);

  return null;
};

const AppContent = () => {
  const location = useLocation();
  const isDashboardRoute = dashboardRoutes.some(route => location.pathname.startsWith(route));
  const isAuthRoute = ['/login', '/register'].includes(location.pathname);

  const isContactPage = location.pathname === '/contact-us';
  const isFullWidthPage = isContactPage || location.pathname === '/career-chavdi';

  if (isAuthRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-textLight">
      {!isDashboardRoute && <Navbar />}
      {!isDashboardRoute && <AnnouncementBanner />}
      {!isDashboardRoute && (
        <div className={`w-full ${isFullWidthPage ? 'p-0 m-0' : location.pathname === '/' ? 'px-6 mt-0' : 'px-6 mt-8'}`}>
          <Routes>


            {/* Public routes */}
            <Route path="/" element={<Home />} />

            {/* Legacy routes (backward compatibility) */}
            <Route path="/users" element={<Users />} />
            <Route path="/users/new" element={<UserForm />} />
            <Route path="/users/edit/:id" element={<UserForm />} />

            {/* Services Routes */}
            <Route path="/services/cloud-devops" element={<CloudDevOpsProgram />} />
            <Route path="/services/ai-ds" element={<AIDataSciencePage />} />
            <Route path="/services/genai" element={<GenAIPage />} />
            <Route path="/services/edge-ai" element={<ServicePage title="Edge AI" />} />
            <Route path="/services/devops" element={<DevOpsPage />} />
            <Route path="/services/cybersecurity" element={<CyberSecurityPage />} />
            <Route path="/contact-us" element={<ContactPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/skill-to-deploy" element={<SkillToDeployPage />} />
            <Route path="/career-chavdi" element={<CareerChavdiPage />} />
            <Route path="/careers" element={<CareersPage />} />
          </Routes>


        </div>
      )}

      {!isDashboardRoute && <Footer />}

      {isDashboardRoute && (
        <Routes>
          {/* Protected routes - Any authenticated user */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Protected routes - Moderator or Admin */}
          <Route
            path="/moderator"
            element={
              <ProtectedRoute roles={['ADMIN', 'MODERATOR']}>
                <Moderator />
              </ProtectedRoute>
            }
          />

          {/* Protected routes - Admin only */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/announcement"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminAnnouncement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminRoles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reviews"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminReviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/mentors"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminMentors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/programs"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminPrograms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contacts"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminContacts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/career-chavdi"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminCareerChavdi />
              </ProtectedRoute>
            }
          />
        </Routes>

      )}
    </div>
  );
};

function App() {
  return (
    <NotificationProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <ApiNotificationSetup />
          <AppContent />
        </Router>
      </AuthProvider>
    </NotificationProvider>
  );
}

export default App;
